import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Eye, Lock, Database, Users, Bell, Globe, FileText, CheckCircle } from 'lucide-react';
import { ENV } from '../config/env';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-20">
      <header className="pf-section-hero py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-white/80 text-lg">
            Last updated: November 11, 2025
          </p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              At <strong>PetsFostering</strong> ("we," "our," or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              By using PetsFostering, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Platform.
            </p>
          </section>

          {/* 1. Information We Collect */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1.1 Information You Provide Directly</h3>
                <p className="mb-2">When you create an account or use our services, you may provide:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, phone number, password, username</li>
                  <li><strong>Profile Information:</strong> Profile picture, bio, location, experience, qualifications</li>
                  <li><strong>Pet Information:</strong> Pet names, types, breeds, ages, health conditions, special needs</li>
                  <li><strong>Demographic Information:</strong> Date of birth, gender, address, occupation, nationality (optional)</li>
                  <li><strong>Financial Information:</strong> Payment methods, transaction history, subscription details</li>
                  <li><strong>Booking Information:</strong> Service details, dates, preferences, special requests</li>
                  <li><strong>Communications:</strong> Messages between users, reviews, ratings, support inquiries</li>
                  <li><strong>Job Tracking Data:</strong> Offline job entries, client information, service records, receipts</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1.2 Information Collected Automatically</h3>
                <p className="mb-2">When you use our Platform, we automatically collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Device Information:</strong> Device type, operating system, browser type, device identifiers</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, time spent, click patterns, search queries</li>
                  <li><strong>Location Data:</strong> IP address, approximate geographic location (with your permission)</li>
                  <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, analytics cookies</li>
                  <li><strong>Log Data:</strong> Access times, error logs, referral URLs</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1.3 Information from Third Parties</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Sign-In:</strong> Name, email, profile picture when you authenticate via Google</li>
                  <li><strong>Social Media:</strong> Public profile information if you link social accounts</li>
                  <li><strong>Payment Processors:</strong> Transaction confirmations, payment status</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p className="font-semibold">We use the information we collect to:</p>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1 Provide and Improve Services</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Create and manage your account</li>
                  <li>Facilitate connections between pet owners and caregivers</li>
                  <li>Process bookings and manage schedules</li>
                  <li>Enable messaging and communication between users</li>
                  <li>Track offline jobs and generate reports (for premium subscribers)</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Improve our Platform features and user experience</li>
                  <li>Develop new services and features</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.2 Safety and Security</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Verify user identities and prevent fraud</li>
                  <li>Detect and prevent abuse, spam, or illegal activities</li>
                  <li>Monitor compliance with our Terms and Conditions</li>
                  <li>Protect the rights, property, and safety of users and PetsFostering</li>
                  <li>Investigate and resolve disputes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.3 Communications</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Send booking confirmations and service updates</li>
                  <li>Notify you of new messages, reviews, or bookings</li>
                  <li>Send administrative messages and policy updates</li>
                  <li>Provide promotional offers and marketing (with your consent)</li>
                  <li>Send newsletters and platform updates (you can opt-out anytime)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.4 Analytics and Research</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Analyze usage patterns and trends</li>
                  <li>Generate aggregate statistics and reports</li>
                  <li>Conduct research to improve services</li>
                  <li>Create business intelligence and insights</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.5 Legal Obligations</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Comply with applicable laws and regulations</li>
                  <li>Respond to legal requests and court orders</li>
                  <li>Enforce our Terms and Conditions</li>
                  <li>Protect against legal liability</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. How We Share Your Information */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[var(--pf-accent)]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. How We Share Your Information</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.1 With Other Users</h3>
                <p>When you use our Platform, certain information is shared to facilitate connections:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Caregivers:</strong> Profile information, reviews, ratings, services offered, availability</li>
                  <li><strong>Pet Owners:</strong> Profile information, pet details (when booking), reviews, ratings</li>
                  <li><strong>Messages:</strong> Communications you send through the Platform are visible to recipients</li>
                  <li><strong>Reviews and Ratings:</strong> Publicly visible on user profiles</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.2 With Service Providers</h3>
                <p>We share information with third-party service providers who help us operate the Platform:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Cloud Hosting:</strong> Firebase, Google Cloud Platform (for data storage)</li>
                  <li><strong>Authentication:</strong> Google Sign-In, Firebase Authentication</li>
                  <li><strong>Analytics:</strong> Google Analytics (anonymized usage data)</li>
                  <li><strong>Payment Processing:</strong> Payment gateway providers</li>
                  <li><strong>Email Services:</strong> Email delivery providers</li>
                  <li><strong>Customer Support:</strong> Support ticket systems</li>
                </ul>
                <p className="mt-2 italic">These providers are contractually obligated to protect your data and use it only for specified purposes.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.3 For Legal Reasons</h3>
                <p>We may disclose your information if required by law or in good faith belief that such action is necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Comply with legal obligations, court orders, or government requests</li>
                  <li>Enforce our Terms and Conditions</li>
                  <li>Investigate potential violations or fraudulent activities</li>
                  <li>Protect the rights, property, or safety of PetsFostering, our users, or the public</li>
                  <li>Respond to emergency situations involving danger to persons or property</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.4 Business Transfers</h3>
                <p>If PetsFostering is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email and/or prominent notice on our Platform before your information is transferred and becomes subject to a different privacy policy.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3.5 With Your Consent</h3>
                <p>We may share your information with third parties when you give us explicit consent to do so.</p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-4">
                <p className="font-semibold text-blue-900 mb-1">Important Note:</p>
                <p className="text-blue-800">We never sell your personal information to third parties for their marketing purposes.</p>
              </div>
            </div>
          </section>

          {/* 4. Data Security */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. Data Security</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Encryption:</strong> Data transmitted over the internet is encrypted using SSL/TLS</li>
                <li><strong>Secure Storage:</strong> Data stored on Firebase Cloud Firestore with security rules</li>
                <li><strong>Access Controls:</strong> Limited employee access on a need-to-know basis</li>
                <li><strong>Authentication:</strong> Secure password hashing and Firebase Authentication</li>
                <li><strong>Monitoring:</strong> Regular security audits and monitoring for suspicious activity</li>
                <li><strong>Backups:</strong> Regular data backups to prevent loss</li>
              </ul>
              <p className="mt-4 italic">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* 5. Your Privacy Rights */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. Your Privacy Rights and Choices</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>You have the following rights regarding your personal information:</p>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.1 Access and Portability</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request a copy of your personal information</li>
                  <li>Download your data in a machine-readable format</li>
                  <li>Access your account settings to view and update information</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.2 Correction and Update</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Update your profile information in account settings</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Request that we correct information we hold about you</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.3 Deletion</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Delete your account at any time through account settings</li>
                  <li>Request deletion of specific information</li>
                  <li>Note: Some information may be retained for legal or legitimate business purposes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.4 Marketing Communications</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Opt-out of promotional emails by clicking "unsubscribe" in any marketing email</li>
                  <li>Manage notification preferences in account settings</li>
                  <li>Note: You will still receive transactional and service-related communications</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.5 Cookies and Tracking</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Adjust browser settings to refuse cookies</li>
                  <li>Use browser "Do Not Track" settings</li>
                  <li>Note: Some Platform features may not function properly without cookies</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5.6 Object to Processing</h3>
                <p>You may object to certain processing of your personal information, particularly for marketing purposes or automated decision-making.</p>
              </div>

              <p className="mt-4 font-semibold">To exercise any of these rights, please contact us at privacy@PetsFostering.com</p>
            </div>
          </section>

          {/* 6. Data Retention */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. Data Retention</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>We retain your personal information for as long as necessary to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide you with our services</li>
                <li>Comply with legal obligations (e.g., tax records, financial reporting)</li>
                <li>Resolve disputes and enforce our agreements</li>
                <li>Maintain business records and analytics</li>
              </ul>
              <p className="mt-4">
                <strong>Account Deletion:</strong> When you delete your account, we will delete or anonymize your personal information within 90 days, except where we are required to retain information for legal or legitimate business purposes.
              </p>
              <p>
                <strong>Backup Systems:</strong> Deleted information may persist in backup systems for up to 30 additional days before permanent deletion.
              </p>
            </div>
          </section>

          {/* 7. Children's Privacy */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">7. Children's Privacy</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>
                Our Platform is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.
              </p>
              <p>
                If we discover that we have collected information from a child under 18, we will delete such information immediately. If you believe we might have information from or about a child under 18, please contact us at privacy@PetsFostering.com.
              </p>
            </div>
          </section>

          {/* 8. International Data Transfers */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-cyan-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">8. International Data Transfers</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>
                Your information may be transferred to and processed in countries other than India, including the United States, where our cloud infrastructure (Firebase, Google Cloud) is located.
              </p>
              <p>
                These countries may have different data protection laws than India. However, we ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
              </p>
            </div>
          </section>

          {/* 9. Third-Party Links */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links and Services</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Our Platform may contain links to third-party websites or services (e.g., Google Sign-In, payment processors) that are not operated by us. We are not responsible for the privacy practices of these third parties.
              </p>
              <p>
                We encourage you to review the privacy policies of any third-party services you access through our Platform.
              </p>
            </div>
          </section>

          {/* 10. Changes to Privacy Policy */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">10. Changes to This Privacy Policy</h2>
            </div>
            <div className="space-y-4 text-gray-700 pl-13">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.
              </p>
              <p>
                <strong>Notification of Changes:</strong> We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Posting a prominent notice on our Platform</li>
                <li>Sending an email to the address associated with your account</li>
                <li>Updating the "Last Updated" date at the top of this policy</li>
              </ul>
              <p className="mt-4">
                Your continued use of the Platform after changes are posted constitutes your acceptance of the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* 11. California Privacy Rights */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Additional Rights for Certain Jurisdictions</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                If you are a resident of California, the European Union, or other jurisdictions with specific privacy laws, you may have additional rights under applicable data protection laws, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Right to know what personal information is collected and how it's used</li>
                <li>Right to request deletion of personal information</li>
                <li>Right to opt-out of the sale of personal information (we do not sell your information)</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-8 border-2 border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us About Privacy</h2>
              <p className="text-gray-700 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Privacy Email:</strong> privacy@PetsFostering.com</p>
                <p><strong>General Support:</strong> support@PetsFostering.com</p>
                <p><strong>Data Protection Officer:</strong> dpo@PetsFostering.com</p>
                <p><strong>Website:</strong> <a href={ENV.APP.URL} className="text-blue-600 hover:text-blue-700">{ENV.APP.DOMAIN}</a></p>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                We will respond to your inquiry within 30 days of receipt.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section>
            <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
              <p className="text-gray-800 font-semibold mb-2">
                ✓ Your Privacy Matters to Us
              </p>
              <p className="text-gray-700">
                By using PetsFostering, you acknowledge that you have read and understood this Privacy Policy. We are committed to protecting your privacy and handling your personal information responsibly.
              </p>
            </div>
          </section>

        </div>

        {/* Back to Top */}
        <div className="text-center mt-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Privacy;

